/**
 * TRH Sample Request - "Request Sample" Button Module
 * ============================================================================
 * Adds a $2 sample swatch of the current fabric / wallpaper / trim product to
 * the cart. Samples are line items of the shared "Sample Swatch" product
 * (handle: trh-sample-swatch) with line item properties identifying the
 * parent product, so the SKU/product linkage survives into the order.
 *
 * Business rules (per the July 2026 dev spec):
 *   - $2 per sample swatch
 *   - Maximum of SAMPLE_LIMIT (10) sample swatches per cart/order
 *   - One sample per distinct parent product (duplicate clicks are rejected)
 *
 * NOTE: The 10-sample limit is enforced client-side at add time and re-checked
 * against the live cart contents on every click. A customer editing quantities
 * on the cart page could exceed it; enforcing at checkout would require
 * Shopify Functions (Plus plan). Staff should verify at fulfillment.
 *
 * @module TRHSampleRequest
 * @version 2.0.0
 *
 * v2.0.0 - Samples are colorway-aware: the currently selected variant is
 *   resolved at click time (URL ?variant= or the product form), and each
 *   sample carries Colorway + Item SKU properties plus a hidden
 *   _sample_image so the cart shows the actual variant's swatch image.
 *   Deduping is per product+colorway (two colorways of one fabric are two
 *   distinct samples); the 10-swatch limit stays cart-wide.
 *
 * Usage (from snippets/trh-sample-button.liquid):
 *   TRHSampleRequest.init({
 *     buttonId: 'trh-sample-btn-<product.id>',
 *     statusId: 'trh-sample-status-<product.id>',
 *     sampleVariantId: 52049626235168,
 *     parent: { title, handle },
 *     variants: [{ id, title, sku, image }, ...]
 *   });
 */
(function () {
  'use strict';

  /** @const {string} Console log prefix */
  var LOG_PREFIX = '[TRHSampleRequest]';

  /** @const {number} Maximum sample swatches allowed per cart/order */
  var SAMPLE_LIMIT = 10;

  /** @const {string} Hidden line item property linking a sample to its parent */
  var PARENT_PROP = '_sample_of';

  /**
   * Fetches the current cart contents.
   * @returns {Promise<Object>} Shopify cart JSON
   */
  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('Could not read cart (HTTP ' + r.status + ')');
        return r.json();
      });
  }

  /**
   * Counts existing sample swatches in the cart (summed across ALL parent
   * products and colorways — the 10-sample limit is cart-wide) and checks
   * whether this exact product+colorway already has a sample in the cart.
   * @param {Object} cart - Shopify cart JSON
   * @param {number} sampleVariantId - Variant ID of the shared sample product
   * @param {string} sampleKey - Dedupe key: "<parentHandle>::<variantId>"
   * @returns {{count: number, alreadySampled: boolean}}
   */
  function analyzeCart(cart, sampleVariantId, sampleKey) {
    var count = 0;
    var alreadySampled = false;

    (cart.items || []).forEach(function (item) {
      if (item.variant_id === sampleVariantId) {
        count += item.quantity;
        if (item.properties && item.properties[PARENT_PROP] === sampleKey) {
          alreadySampled = true;
        }
      }
    });

    return { count: count, alreadySampled: alreadySampled };
  }

  /**
   * Resolves the currently selected variant of the parent product at click
   * time. The customer can change colorways without a page reload, so this
   * cannot be resolved when the snippet renders. Resolution order:
   *   1. `?variant=` URL parameter (the theme updates it on variant change)
   *   2. The product form's `input[name="id"]` whose value is a known variant
   *   3. The first variant (single-variant products land here)
   * @param {Array<Object>} variants - [{ id, title, sku, image }, ...]
   * @returns {Object|null} The selected variant entry, or null if none known
   */
  function resolveCurrentVariant(variants) {
    if (!variants || !variants.length) return null;

    var byId = {};
    variants.forEach(function (v) { byId[String(v.id)] = v; });

    var urlVariant = new URLSearchParams(window.location.search).get('variant');
    if (urlVariant && byId[urlVariant]) return byId[urlVariant];

    var inputs = document.querySelectorAll('form[action*="/cart/add"] input[name="id"]');
    for (var i = 0; i < inputs.length; i++) {
      if (byId[inputs[i].value]) return byId[inputs[i].value];
    }

    return variants[0];
  }

  /**
   * Refreshes the header cart icon bubble without a page reload.
   * Uses the Shopify Section Rendering API (Dawn/Craft theme pattern).
   * Fails silently — the bubble updates on the next navigation anyway.
   */
  function refreshCartBubble() {
    fetch('/?sections=cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = sections['cart-icon-bubble'];
        var target = document.getElementById('cart-icon-bubble');
        if (html && target) {
          /* The Section Rendering API wraps the section's inner content in a
           * <div class="shopify-section"> wrapper — unwrap it (Dawn pattern). */
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          var fresh = tmp.querySelector('.shopify-section') || tmp.firstElementChild;
          if (fresh) target.innerHTML = fresh.innerHTML;
        }
      })
      .catch(function (err) {
        console.warn(LOG_PREFIX, 'Cart bubble refresh failed (non-fatal):', err);
      });
  }

  /**
   * Shows a status message below the button.
   * @param {HTMLElement} statusEl - Status container element
   * @param {string} message - Text to display
   * @param {boolean} isError - Whether to style as an error
   */
  function showStatus(statusEl, message, isError) {
    if (!statusEl) return;
    statusEl.textContent = message;
    statusEl.style.display = 'block';
    statusEl.classList.toggle('trh-sample__status--error', !!isError);
    statusEl.classList.toggle('trh-sample__status--success', !isError);
  }

  /**
   * Handles a Request Sample click: re-checks the live cart against the
   * limit and duplicate rules, then adds one sample line item.
   * @param {Object} config - Module config (see init)
   * @param {HTMLElement} button - The clicked button
   * @param {HTMLElement} statusEl - Status message element
   */
  async function handleClick(config, button, statusEl) {
    button.disabled = true;
    var originalLabel = button.textContent;
    button.textContent = 'Adding…';

    try {
      /* Resolve the colorway the customer is currently viewing */
      var variant = resolveCurrentVariant(config.variants);
      var sampleKey = config.parent.handle + '::' + (variant ? variant.id : 'default');

      var cart = await fetchCart();
      var state = analyzeCart(cart, config.sampleVariantId, sampleKey);

      if (state.alreadySampled) {
        showStatus(statusEl, 'A sample of this colorway is already in your cart.', true);
        button.textContent = originalLabel;
        button.disabled = false;
        return;
      }

      if (state.count >= SAMPLE_LIMIT) {
        showStatus(
          statusEl,
          'Sample limit reached — a maximum of ' + SAMPLE_LIMIT +
          ' sample swatches per order (across all colors and patterns).',
          true
        );
        button.textContent = originalLabel;
        button.disabled = false;
        return;
      }

      /* Visible properties show on cart/checkout/order; underscore-prefixed
       * properties are hidden but preserved for fulfillment linkage and for
       * the cart page to render the variant's swatch image. */
      var properties = {
        'Sample of': config.parent.title
      };
      if (variant && variant.title && variant.title !== 'Default Title') {
        properties['Colorway'] = variant.title;
      }
      if (variant && variant.sku) properties['Item SKU'] = variant.sku;
      properties[PARENT_PROP] = sampleKey;
      if (variant && variant.image) properties['_sample_image'] = variant.image;

      var addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: config.sampleVariantId,
            quantity: 1,
            properties: properties
          }]
        })
      });

      if (!addResponse.ok) {
        var errData = await addResponse.json().catch(function () { return {}; });
        throw new Error(errData.description || errData.message || 'Could not add sample to cart.');
      }

      console.log(LOG_PREFIX, 'Sample added:', config.parent.title);
      showStatus(
        statusEl,
        'Sample added to cart (' + (state.count + 1) + ' of ' + SAMPLE_LIMIT + ').',
        false
      );
      button.textContent = 'Sample Added ✓';
      refreshCartBubble();

      /* Re-enable after a moment so the customer can see the confirmation
       * but still request again if they remove it from the cart. */
      setTimeout(function () {
        button.textContent = originalLabel;
        button.disabled = false;
      }, 2500);
    } catch (err) {
      console.error(LOG_PREFIX, err);
      showStatus(statusEl, err.message || 'Something went wrong. Please try again.', true);
      button.textContent = originalLabel;
      button.disabled = false;
    }
  }

  /* ==========================================================================
   * PUBLIC API
   * ========================================================================== */

  window.TRHSampleRequest = {
    /**
     * Initializes a Request Sample button.
     * @param {Object} config
     * @param {string} config.buttonId - DOM ID of the button element
     * @param {string} config.statusId - DOM ID of the status message element
     * @param {number} config.sampleVariantId - Variant ID of the sample product
     * @param {Object} config.parent - Parent product info
     * @param {string} config.parent.title - Parent product title
     * @param {string} config.parent.handle - Parent product handle
     * @param {Array<Object>} [config.variants] - Parent variants for
     *   click-time colorway resolution: [{ id, title, sku, image }, ...]
     */
    init: function (config) {
      if (!config || !config.buttonId || !config.sampleVariantId || !config.parent) {
        console.error(LOG_PREFIX, 'init: buttonId, sampleVariantId and parent are required.');
        return;
      }

      var button = document.getElementById(config.buttonId);
      var statusEl = config.statusId ? document.getElementById(config.statusId) : null;
      if (!button) {
        console.error(LOG_PREFIX, 'init: button #' + config.buttonId + ' not found.');
        return;
      }

      config.sampleVariantId = Number(config.sampleVariantId);

      button.addEventListener('click', function (event) {
        event.preventDefault();
        handleClick(config, button, statusEl);
      });
    }
  };
})();
